import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Pill } from "@/components/badges";
import DateRangePicker from "@/components/DateRangePicker";
import { Plus, X, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Tasks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: searchParams.get("date_from") || "",
    to: searchParams.get("date_to") || "",
  });

  useEffect(() => {
    const p = new URLSearchParams();
    if (dateRange.from) p.set("date_from", dateRange.from);
    if (dateRange.to) p.set("date_to", dateRange.to);
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line
  }, [dateRange.from, dateRange.to]);

  const load = async () => {
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    const [t, u] = await Promise.all([api.get("/tasks", { params }), api.get("/users")]);
    setTasks(t.data); setUsers(u.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateRange.from, dateRange.to]);

  const today = new Date().toISOString().slice(0, 10);
  const overdue = tasks.filter((t) => t.status !== "Completed" && t.due_date && t.due_date < today);
  const dueToday = tasks.filter((t) => t.status !== "Completed" && t.due_date === today);
  const upcoming = tasks.filter((t) => t.status !== "Completed" && t.due_date && t.due_date > today);
  const completed = tasks.filter((t) => t.status === "Completed");

  const filtered = useMemo(() => {
    switch (filter) {
      case "overdue": return overdue;
      case "today": return dueToday;
      case "upcoming": return upcoming;
      case "completed": return completed;
      default: return tasks;
    }
  }, [filter, tasks, overdue, dueToday, upcoming, completed]);

  const setStatus = async (id, status) => { await api.patch(`/tasks/${id}`, { status }); load(); };
  const del = async (id) => { await api.delete(`/tasks/${id}`); load(); toast.success("Task deleted"); };

  return (
    <div className="p-8" data-testid="tasks-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyb-label">Productivity</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Tasks & Follow-ups</h1>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker value={dateRange} onChange={setDateRange} defaultPreset="all" />
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> New Task
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <Card icon={AlertTriangle} tone="rose" label="Overdue" value={overdue.length} active={filter === "overdue"} onClick={() => setFilter("overdue")} />
        <Card icon={Clock} tone="amber" label="Due today" value={dueToday.length} active={filter === "today"} onClick={() => setFilter("today")} />
        <Card icon={Clock} tone="sky" label="Upcoming" value={upcoming.length} active={filter === "upcoming"} onClick={() => setFilter("upcoming")} />
        <Card icon={CheckCircle2} tone="emerald" label="Completed" value={completed.length} active={filter === "completed"} onClick={() => setFilter("completed")} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setFilter("all")} className={`text-xs px-3 py-1 rounded-full ${filter === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>All ({tasks.length})</button>
      </div>

      <div className="eyb-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
              <th className="px-4 py-3">Task</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3">Assigned</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900">{t.title}</div>
                  {t.description && <div className="text-xs text-slate-500">{t.description}</div>}
                </td>
                <td className="px-4 py-3">
                  <Pill className={t.priority === "High" ? "bg-rose-50 text-rose-700 border border-rose-200" : t.priority === "Medium" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-slate-100 text-slate-600 border border-slate-200"}>{t.priority}</Pill>
                </td>
                <td className="px-4 py-3 text-sm text-slate-700">{fmtDate(t.due_date)}</td>
                <td className="px-4 py-3 text-xs text-slate-600">{users.find((u) => u.id === t.assigned_to)?.name || "—"}</td>
                <td className="px-4 py-3">
                  <select value={t.status} onChange={(e) => setStatus(t.id, e.target.value)} className="px-2 py-1 border border-slate-200 rounded-md text-xs bg-white">
                    {["Open", "In Progress", "Completed"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(t.id)} className="text-xs text-rose-600 hover:underline">Delete</button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400 text-sm">No tasks here.</td></tr>}
          </tbody>
        </table>
      </div>

      {showNew && <NewTaskModal users={users} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); toast.success("Task created"); }} />}
    </div>
  );
}

function Card({ icon: Icon, tone, label, value, active, onClick }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return (
    <button onClick={onClick} className={`eyb-card eyb-card-hover p-4 text-left ${active ? "ring-2 ring-[#29ABE2]" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="eyb-label">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${tones[tone]}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{value}</div>
    </button>
  );
}

function NewTaskModal({ users, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", due_date: "", priority: "Medium", assigned_to: "", status: "Open" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/tasks", form); onSaved(); } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">New Task</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <label className="block"><span className="eyb-label">Title</span>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </label>
          <label className="block"><span className="eyb-label">Description</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" rows={2} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="eyb-label">Due Date</span>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </label>
            <label className="block"><span className="eyb-label">Priority</span>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                {["Low", "Medium", "High"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label className="block col-span-2"><span className="eyb-label">Assign To</span>
              <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                <option value="">— me —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">{saving ? "Saving…" : "Create"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
